/**
 * Lich's Loom — theme.js
 * Vanilla JS only. No external dependencies.
 *
 * Components:
 *  Overlay          — shared background overlay manager
 *  CartDrawer       — slide-in cart with live rendering
 *  MobileMenu       — slide-in mobile navigation
 *  StickyHeader     — hide-on-scroll sticky header
 *  SearchOverlay    — predictive search overlay
 *  ProductForm      — AJAX add-to-cart
 *  VariantPicker    — variant selection + price/availability update
 *  QuickAdd         — product card quick-add handler
 *  QuantityInput    — +/- quantity controls
 *  CollapsibleGroup — accordion / FAQ panels
 *  ProductTabs      — product detail tab switching
 *  ScrollAnimations — Intersection Observer entrance animations
 *  AnnouncementBar  — session-dismissible banner
 */

'use strict';

/* ============================================================
   Utilities
   ============================================================ */

function qs(sel, ctx = document) { return ctx.querySelector(sel); }
function qsa(sel, ctx = document) { return [...ctx.querySelectorAll(sel)]; }

function debounce(fn, wait = 300) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Delegated event listener.
 * on(element, 'click', '.selector', handler)  ← handler receives (event, matchedElement)
 * on(element, 'click', handler)               ← no delegation
 */
function on(el, event, selectorOrHandler, handler) {
  if (typeof selectorOrHandler === 'function') {
    el.addEventListener(event, selectorOrHandler);
    return;
  }
  el.addEventListener(event, function (e) {
    const target = e.target.closest(selectorOrHandler);
    if (target && this.contains(target)) handler.call(target, e, target);
  });
}

/** Escape a string for safe interpolation into innerHTML. */
function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

/** Localised strings injected by layout/theme.liquid; fallbacks keep the brand voice. */
const STRINGS = Object.assign({
  addToCart: 'Equip This Item',
  soldOut: 'Sold Out',
  unavailable: 'Unavailable',
  adding: 'Equipping…',
  added: 'Added!',
  quickAddFailed: 'Failed — try again',
  cartError: 'There was an error adding to your cart. Please try again.',
  cartEmpty: 'Your bag of holding is empty',
  cartEmptySubtext: 'No items have been enchanted into your cart yet.',
  cartBrowse: 'Browse the Loom',
  cartUpdated: 'Your cart has been updated.',
  cartLabel: 'Cart',
  cartCount: '{{ count }} items',
  freeShippingUnlocked: "You've unlocked free UK shipping!",
  freeShippingProgress: 'Spend {{ amount }} more for free UK shipping',
  noResults: 'No results found',
  decreaseQuantity: 'Decrease quantity',
  increaseQuantity: 'Increase quantity',
  removeItem: 'Remove {{ title }}',
}, window.themeStrings || {});

/** Interpolate {{ key }} placeholders in a translated string. */
function tString(str, replacements = {}) {
  return String(str).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (
    key in replacements ? replacements[key] : ''
  ));
}

/** Locale-aware routes injected by layout/theme.liquid. */
const ROUTES = Object.assign({
  root: '/',
  cart_url: '/cart',
  cart_add_url: '/cart/add',
  cart_change_url: '/cart/change',
  cart_update_url: '/cart/update',
  collections_url: '/collections',
  predictive_search_url: '/search/suggest',
}, window.routes || {});

/**
 * Format cents using the shop's money format (injected as
 * window.shopMoneyFormat). Falls back to Intl with the shop currency.
 */
function formatMoney(cents, format = window.shopMoneyFormat) {
  if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

  function withDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const units = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? units + decimal + parts[1] : units;
  }

  if (typeof format === 'string' && placeholderRegex.test(format)) {
    let value;
    switch (format.match(placeholderRegex)[1]) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  return new Intl.NumberFormat(document.documentElement.lang || undefined, {
    style: 'currency',
    currency: window.shopCurrency || 'GBP',
  }).format(cents / 100);
}

/** Resize a Shopify CDN image URL via width query param. */
function imageUrl(src, width) {
  if (!src) return null;
  try {
    const u = new URL(src, window.location.origin);
    u.searchParams.set('width', width);
    return u.toString();
  } catch (_err) {
    return src;
  }
}

/** Announce a message to screen readers via the global live region. */
function announce(message) {
  const region = document.getElementById('a11y-refresh-page-message');
  if (!region) return;
  region.textContent = '';
  requestAnimationFrame(() => { region.textContent = message; });
}

/* ---- Dialog helpers: inert state + focus management ---- */

function dialogOpen(el) {
  el.inert = false;
  el.setAttribute('aria-hidden', 'false');
  el._lastFocus = document.activeElement;
}

function dialogClose(el) {
  el.setAttribute('aria-hidden', 'true');
  el.inert = true;
  if (el._lastFocus && typeof el._lastFocus.focus === 'function') {
    el._lastFocus.focus();
    el._lastFocus = null;
  }
}

function focusFirst(el) {
  const target = qs('input:not([type="hidden"]), button, [href], select, textarea, [tabindex]:not([tabindex="-1"])', el);
  target?.focus();
}

/* ============================================================
   Overlay — shared background layer
   ============================================================ */

const Overlay = {
  el: null,
  _stack: 0,

  init() {
    this.el = document.getElementById('overlay');
    this.el?.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('overlay:clicked'));
    });
  },

  show() {
    this._stack++;
    this.el?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  },

  hide() {
    this._stack = Math.max(0, this._stack - 1);
    if (this._stack === 0) {
      this.el?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }
  },

  forceHide() {
    this._stack = 0;
    this.el?.classList.remove('is-visible');
    document.body.style.overflow = '';
  },
};

/* ============================================================
   CartDrawer
   ============================================================ */

class CartDrawer {
  constructor() {
    this.el         = document.getElementById('cart-drawer');
    this.itemsWrap  = qs('[data-cart-items]', this.el);
    this.totalEl    = qs('[data-cart-total]', this.el);
    this.errorEl    = qs('[data-cart-error]', this.el);
    this.shippingBar = qs('[data-cart-shipping-bar]', this.el);
    this.shippingText = qs('[data-cart-shipping-text]', this.el);
    this.shippingFill = qs('[data-cart-shipping-fill]', this.el);
    this.isOpen     = false;
    this._threshold = window.freeShippingAmount || 0;

    if (!this.el) return;
    this.el.inert = true;
    this._bind();
    this._syncCount(window.cartItemCount || 0);
  }

  _bind() {
    on(document, 'click', '[data-cart-open]', (e) => {
      e.preventDefault();
      this.open();
    });
    on(document, 'click', '[data-cart-close]', () => this.close());
    document.addEventListener('overlay:clicked', () => { if (this.isOpen) this.close(); });
    document.addEventListener('cart:updated',    () => { if (this.isOpen) this._fetch(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });

    /* Item quantity / remove — delegated inside items wrapper */
    on(this.el, 'click', '[data-qty-decrease]', (e, btn) => {
      const row = btn.closest('[data-key]');
      if (!row) return;
      this._change(row.dataset.key, this._displayQty(row) - 1);
    });
    on(this.el, 'click', '[data-qty-increase]', (e, btn) => {
      const row = btn.closest('[data-key]');
      if (!row) return;
      this._change(row.dataset.key, this._displayQty(row) + 1);
    });
    on(this.el, 'click', '[data-item-remove]', (e, btn) => {
      this._change(btn.dataset.itemRemove, 0);
    });
  }

  _displayQty(row) {
    return parseInt(qs('[data-qty-display]', row)?.textContent || '1', 10);
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.el.classList.add('is-open');
    dialogOpen(this.el);
    Overlay.show();
    this._fetch();
    focusFirst(this.el);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.el.classList.remove('is-open');
    dialogClose(this.el);
    Overlay.hide();
  }

  async _fetch() {
    try {
      const res  = await fetch(`${ROUTES.cart_url}.js`);
      if (!res.ok) throw new Error(`Cart fetch failed (${res.status})`);
      this._apply(await res.json());
    } catch (err) {
      console.error('[CartDrawer] fetch:', err);
    }
  }

  _apply(cart) {
    this._render(cart);
    this._syncCount(cart.item_count);
    this._updateTotal(cart.total_price);
    this._updateShippingBar(cart.total_price);
  }

  _render(cart) {
    if (!this.itemsWrap) return;
    this.itemsWrap.innerHTML = cart.item_count === 0
      ? this._emptyHTML()
      : cart.items.map(item => this._itemHTML(item)).join('');
  }

  _itemHTML(item) {
    const rawSrc = item.featured_image?.url || item.image || null;
    const imgSrc = rawSrc ? imageUrl(rawSrc, 160) : null;
    const title  = escapeHtml(item.product_title);
    const variantLine = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>` : '';

    return /* html */`
      <div class="cart-item" data-key="${escapeHtml(item.key)}">
        <div class="cart-item__image">
          ${imgSrc ? `<img src="${imgSrc}" alt="${title}" width="80" height="100" loading="lazy">` : ''}
        </div>
        <div class="cart-item__details">
          <p class="cart-item__title">${title}</p>
          ${variantLine}
          <p class="cart-item__price">${formatMoney(item.final_line_price)}</p>
          <div class="cart-item__row">
            <div class="quantity-wrapper quantity-wrapper--compact">
              <button class="quantity-wrapper__btn" data-qty-decrease aria-label="${escapeHtml(STRINGS.decreaseQuantity)}">&minus;</button>
              <span class="quantity-wrapper__input quantity-wrapper__display" data-qty-display>${item.quantity}</span>
              <button class="quantity-wrapper__btn" data-qty-increase aria-label="${escapeHtml(STRINGS.increaseQuantity)}">+</button>
            </div>
            <button
              class="cart-item__remove"
              data-item-remove="${escapeHtml(item.key)}"
              aria-label="${escapeHtml(tString(STRINGS.removeItem, { title: item.product_title }))}"
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                <path d="M1 1l11 11M12 1L1 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `.trim();
  }

  _emptyHTML() {
    return /* html */`
      <div class="cart-empty">
        <p class="cart-empty__icon" aria-hidden="true">⚔</p>
        <p class="cart-empty__title">${escapeHtml(STRINGS.cartEmpty)}</p>
        <p class="cart-empty__text">${escapeHtml(STRINGS.cartEmptySubtext)}</p>
        <a href="${escapeHtml(ROUTES.collections_url)}" class="btn btn--secondary cart-empty__cta">${escapeHtml(STRINGS.cartBrowse)}</a>
      </div>
    `.trim();
  }

  async _change(key, quantity) {
    this._showError('');
    try {
      const res = await fetch(`${ROUTES.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const data = await res.json();
      if (!res.ok || !Array.isArray(data.items)) {
        this._showError(data.description || data.message || STRINGS.cartError);
        this._fetch();
        return;
      }
      this._apply(data);
      announce(STRINGS.cartUpdated);
    } catch (err) {
      console.error('[CartDrawer] change:', err);
      this._showError(STRINGS.cartError);
    }
  }

  _showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = !message;
  }

  _syncCount(count) {
    qsa('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.hidden      = count === 0;
    });
    const label = `${STRINGS.cartLabel} — ${tString(STRINGS.cartCount, { count })}`;
    qsa('[data-cart-open]').forEach(el => el.setAttribute('aria-label', label));
  }

  _updateTotal(cents) {
    if (this.totalEl) this.totalEl.textContent = formatMoney(cents);
  }

  _updateShippingBar(totalCents) {
    if (!this.shippingBar || !window.freeShippingEnabled || !this._threshold) return;
    this.shippingBar.style.display = '';
    if (totalCents >= this._threshold) {
      this.shippingText.textContent = STRINGS.freeShippingUnlocked;
      this.shippingFill.style.width = '100%';
      this.shippingFill.style.background = 'var(--rarity-uncommon)';
    } else {
      const remaining = formatMoney(this._threshold - totalCents);
      this.shippingText.textContent = tString(STRINGS.freeShippingProgress, { amount: remaining });
      const pct = Math.min(100, (totalCents / this._threshold) * 100).toFixed(1);
      this.shippingFill.style.width = pct + '%';
      this.shippingFill.style.background = 'var(--color-accent)';
    }
  }
}

/* ============================================================
   MobileMenu
   ============================================================ */

class MobileMenu {
  constructor() {
    this.el     = document.getElementById('mobile-menu');
    this.isOpen = false;
    if (!this.el) return;
    this.el.inert = true;
    this._bind();
  }

  _bind() {
    on(document, 'click', '[data-mobile-menu-open]',  () => this.open());
    on(document, 'click', '[data-mobile-menu-close]', () => this.close());
    document.addEventListener('overlay:clicked', () => { if (this.isOpen) this.close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });

    /* Sub-nav toggles */
    on(this.el, 'click', '[data-mobile-subnav-toggle]', (e, toggle) => {
      const item = toggle.closest('.mobile-menu__nav-item');
      if (!item) return;
      const isOpen = item.classList.contains('is-open');
      qsa('.mobile-menu__nav-item.is-open', this.el).forEach(el => {
        el.classList.remove('is-open');
        qs('[data-mobile-subnav-toggle]', el)?.setAttribute('aria-expanded', 'false');
      });
      if (!isOpen) {
        item.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.el.classList.add('is-open');
    dialogOpen(this.el);
    qs('[data-mobile-menu-open]')?.setAttribute('aria-expanded', 'true');
    Overlay.show();
    focusFirst(this.el);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.el.classList.remove('is-open');
    dialogClose(this.el);
    qs('[data-mobile-menu-open]')?.setAttribute('aria-expanded', 'false');
    Overlay.hide();
  }
}

/* ============================================================
   StickyHeader
   ============================================================ */

class StickyHeader {
  constructor() {
    this.el          = document.getElementById('site-header');
    this._lastScrollY = 0;
    this._ticking    = false;
    if (!this.el) return;
    window.addEventListener('scroll', () => this._onScroll(), { passive: true });
  }

  _onScroll() {
    if (!this._ticking) {
      requestAnimationFrame(() => { this._update(); this._ticking = false; });
      this._ticking = true;
    }
  }

  _update() {
    const y = window.scrollY;
    this.el.classList.toggle('is-scrolled', y > 60);
    /* Hide on scroll down, show on scroll up */
    if (y > this._lastScrollY && y > 250) {
      this.el.classList.add('is-hidden');
    } else {
      this.el.classList.remove('is-hidden');
    }
    this._lastScrollY = y;
  }
}

/* ============================================================
   SearchOverlay
   ============================================================ */

class SearchOverlay {
  constructor() {
    this.el      = document.getElementById('search-overlay');
    this.input   = qs('[data-search-input]',   this.el);
    this.results = qs('[data-search-results]', this.el);
    this.isOpen  = false;
    if (!this.el) return;
    this.el.inert = true;
    this._debouncedFetch = debounce((q) => this._fetch(q), 350);
    this._bind();
  }

  _bind() {
    on(document, 'click', '[data-search-open]',  (e) => { e.preventDefault(); this.open(); });
    on(document, 'click', '[data-search-close]', () => this.close());
    document.addEventListener('overlay:clicked', () => { if (this.isOpen) this.close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });

    this.input?.addEventListener('input', (e) => {
      const q = e.target.value.trim();
      if (q.length < 2) { this._clearResults(); return; }
      this._debouncedFetch(q);
    });

    /* Submit the form if Enter is pressed (falls back to full search page) */
    this.input?.closest('form')?.addEventListener('submit', () => this.close());

    /* Close when a predictive result is chosen */
    if (this.results) on(this.results, 'click', 'a', () => this.close());
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.el.classList.add('is-open');
    dialogOpen(this.el);
    Overlay.show();
    setTimeout(() => this.input?.focus(), 60);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.el.classList.remove('is-open');
    this._clearResults();
    if (this.input) this.input.value = '';
    dialogClose(this.el);
    Overlay.hide();
  }

  _clearResults() {
    if (this.results) this.results.innerHTML = '';
  }

  async _fetch(q) {
    if (!window.predictiveEnabled) return;
    try {
      const url = `${ROUTES.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
      const res  = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      this._render(data.resources?.results?.products || []);
    } catch (err) {
      console.error('[SearchOverlay]', err);
    }
  }

  _render(products) {
    if (!this.results) return;
    if (!products.length) {
      this.results.innerHTML = `<p class="search-no-results">${escapeHtml(STRINGS.noResults)}</p>`;
      return;
    }
    this.results.innerHTML = products.map(p => {
      const price = p.price_min ? formatMoney(p.price_min) : '';
      const img   = p.featured_image?.url ? imageUrl(p.featured_image.url, 100) : null;
      return /* html */`
        <a href="${escapeHtml(p.url)}" class="search-result-item">
          ${img ? `<img src="${img}" alt="${escapeHtml(p.title)}" width="50" height="62" loading="lazy">` : ''}
          <div>
            <p class="search-result-title">${escapeHtml(p.title)}</p>
            ${price ? `<p class="search-result-price">${price}</p>` : ''}
          </div>
        </a>
      `;
    }).join('');
  }
}

/* ============================================================
   ProductForm — AJAX add to cart
   ============================================================ */

class ProductForm {
  constructor(form) {
    this.form    = form;
    this.btn     = qs('[data-atc-btn]', form) || qs('[type="submit"]', form);
    this.btnText = this.btn && qs('[data-btn-text]', this.btn);
    this.errorEl = qs('[data-form-error]', form);
    this._bind();
  }

  _bind() {
    this.form.addEventListener('submit', (e) => { e.preventDefault(); this._submit(); });
  }

  async _submit() {
    if (!this.btn || this.btn.disabled) return;
    this._setLoading(true);
    try {
      const res = await fetch(`${ROUTES.cart_add_url}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || STRINGS.cartError);
      }
      this._setSuccess();
      announce(STRINGS.cartUpdated);
      document.dispatchEvent(new CustomEvent('cart:updated'));
      window._cartDrawer?.open();
    } catch (err) {
      this._setError(err.message);
    } finally {
      this._setLoading(false);
    }
  }

  _setLoading(loading) {
    if (!this.btn) return;
    this.btn.disabled = loading;
    this.btn.classList.toggle('is-loading', loading);
  }

  _setSuccess() {
    if (!this.btnText) return;
    const orig = this.btnText.textContent;
    this.btnText.textContent = STRINGS.added;
    this.btn.classList.add('is-success');
    setTimeout(() => {
      this.btnText.textContent = orig;
      this.btn.classList.remove('is-success');
    }, 2000);
  }

  _setError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
    setTimeout(() => { this.errorEl.hidden = true; }, 5000);
  }
}

/* ============================================================
   VariantPicker
   ============================================================ */

class VariantPicker {
  constructor(el) {
    this.el       = el;
    this.form     = (el.dataset.formId && document.getElementById(el.dataset.formId))
      || el.closest('form')
      || el.closest('[data-product-form]')
      || qs('[data-product-form]');
    this.section  = el.closest('[data-product-info]') || document;
    this.variants = JSON.parse(el.dataset.variants || '[]');
    this.current  = null;
    this._init();
    this._bind();
  }

  _init() {
    /* Find initial selected variant from the hidden ID input */
    const idInput = this.form && qs('input[name="id"]', this.form);
    const id      = idInput ? parseInt(idInput.value, 10) : null;
    this.current  = this.variants.find(v => v.id === id) || this.variants[0];
    this._refreshAvailability();
  }

  _bind() {
    on(this.el, 'change', 'input[type="radio"]', () => this._update());
    on(this.el, 'change', 'select',              () => this._update());
  }

  _selectedOptions() {
    return qsa('.variant-option', this.el).map(group => {
      return (
        qs('input[type="radio"]:checked', group)?.value ||
        qs('select', group)?.value ||
        ''
      );
    });
  }

  _update() {
    const opts    = this._selectedOptions();
    const variant = this.variants.find(v =>
      v.options.every((o, i) => o === opts[i])
    );

    const idInput = this.form && qs('input[name="id"]', this.form);
    const atcBtn  = qs('[data-atc-btn]', this.form || this.section);
    const atcText = atcBtn && qs('[data-btn-text]', atcBtn);

    this._refreshAvailability();

    if (!variant) {
      /* Selected combination maps to no variant: block purchase, say so. */
      this.current = null;
      if (idInput) idInput.value = '';
      if (atcBtn) {
        atcBtn.disabled = true;
        if (atcText) atcText.textContent = STRINGS.unavailable;
      }
      return;
    }

    this.current = variant;
    if (idInput) idInput.value = variant.id;

    /* Reflect the variant in the URL so refresh/share keeps the selection */
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (_err) { /* noop */ }

    /* Update displayed price (scoped to the product info container) */
    const priceEl   = qs('[data-product-price]', this.section);
    const compareEl = qs('[data-product-compare-price]', this.section);
    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (compareEl) {
      if (variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.hidden      = false;
      } else {
        compareEl.hidden = true;
      }
    }

    /* Update ATC button */
    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      if (atcText) atcText.textContent = variant.available ? STRINGS.addToCart : STRINGS.soldOut;
    }

    /* Dispatch for gallery to react */
    this.el.dispatchEvent(
      new CustomEvent('variant:changed', { detail: { variant }, bubbles: true })
    );
  }

  /**
   * Mark each option value unavailable when no in-stock variant matches it
   * combined with the currently selected values of the other options.
   */
  _refreshAvailability() {
    const selected = this._selectedOptions();
    qsa('.variant-option', this.el).forEach((group, groupIndex) => {
      qsa('input[type="radio"]', group).forEach(radio => {
        const candidate = selected.slice();
        candidate[groupIndex] = radio.value;
        const match = this.variants.find(v =>
          v.options.every((o, i) => o === candidate[i])
        );
        const available = Boolean(match && match.available);
        radio.toggleAttribute('data-unavailable', !available);
        radio.closest('.variant-btn')?.classList.toggle('is-unavailable', !available);
      });
    });
  }
}

/* ============================================================
   QuantityInput
   ============================================================ */

function initQuantityInputs() {
  on(document, 'click', '[data-quantity-decrease]', (e, btn) => {
    const wrap  = btn.closest('.quantity-wrapper');
    const input = wrap && qs('[data-quantity-input]', wrap);
    if (!input) return;
    const min = parseInt(input.min, 10) || 1;
    const val = parseInt(input.value,  10) || 1;
    if (val > min) {
      input.value = val - 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  on(document, 'click', '[data-quantity-increase]', (e, btn) => {
    const wrap  = btn.closest('.quantity-wrapper');
    const input = wrap && qs('[data-quantity-input]', wrap);
    if (!input) return;
    const max = parseInt(input.max, 10) || Infinity;
    const val = parseInt(input.value, 10) || 1;
    if (val < max) {
      input.value = val + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
}

/* ============================================================
   CollapsibleGroup (accordion / FAQ)
   ============================================================ */

function initCollapsibles() {
  on(document, 'click', '[data-collapsible-trigger]', (e, trigger) => {
    const item   = trigger.closest('[data-collapsible-item]');
    if (!item) return;
    const body   = qs('[data-collapsible-body]', item);
    const parent = item.closest('[data-collapsible-group]');
    const isOpen = item.classList.contains('is-open');

    /* Close siblings */
    if (parent) {
      qsa('[data-collapsible-item].is-open', parent).forEach(sib => {
        sib.classList.remove('is-open');
        const sibBody = qs('[data-collapsible-body]', sib);
        if (sibBody) sibBody.style.height = '0';
        qs('[data-collapsible-trigger]', sib)?.setAttribute('aria-expanded', 'false');
      });
    }

    if (!isOpen && body) {
      item.classList.add('is-open');
      body.style.height = body.scrollHeight + 'px';
      trigger.setAttribute('aria-expanded', 'true');
    }
  });
}

/* ============================================================
   Product Tabs
   ============================================================ */

function initProductTabs() {
  qsa('.product-tabs').forEach(tabs => {
    const btns   = qsa('.product-tabs__btn',   tabs);
    const panels = qsa('.product-tabs__panel', tabs);

    btns.forEach((btn, i) => {
      btn.addEventListener('click', () => {
        btns.forEach(b   => b.classList.remove('is-active'));
        panels.forEach(p => p.classList.remove('is-active'));
        btn.classList.add('is-active');
        panels[i]?.classList.add('is-active');
      });
    });

    /* Activate first tab by default */
    const hasActive = btns.some(b => b.classList.contains('is-active'));
    if (btns[0] && !hasActive) {
      btns[0].classList.add('is-active');
      panels[0]?.classList.add('is-active');
    }
  });
}

/* ============================================================
   Header nav dropdowns — sync aria-expanded with :hover/:focus-within
   ============================================================ */

function initNavDropdowns() {
  qsa('.header__nav-item.has-dropdown').forEach(item => {
    const link = qs('.header__nav-link', item);
    if (!link) return;
    const setOpen = (open) => link.setAttribute('aria-expanded', open ? 'true' : 'false');
    item.addEventListener('mouseenter', () => setOpen(true));
    item.addEventListener('mouseleave', () => setOpen(false));
    item.addEventListener('focusin',  () => setOpen(true));
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) setOpen(false);
    });
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { setOpen(false); link.focus(); }
    });
  });
}

/* ============================================================
   Scroll Animations
   ============================================================ */

function initScrollAnimations() {
  const els = qsa('[data-animate]');
  if (!els.length) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!('IntersectionObserver' in window) || reduceMotion) {
    els.forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
  );
  els.forEach(el => observer.observe(el));
}

/* ============================================================
   Announcement Bar
   ============================================================ */

function initAnnouncementBar() {
  const bar = qs('[data-announcement-bar]');
  if (!bar) return;
  try {
    if (sessionStorage.getItem('ll-anm-dismissed')) {
      bar.hidden = true;
      return;
    }
  } catch (_err) { /* storage unavailable */ }
  qs('[data-announcement-close]', bar)?.addEventListener('click', () => {
    bar.hidden = true;
    try { sessionStorage.setItem('ll-anm-dismissed', '1'); } catch (_err) { /* noop */ }
  });
}

/* ============================================================
   Filter sidebar mobile toggle + auto-apply
   ============================================================ */

function initFilterToggle() {
  const toggle  = qs('[data-filter-toggle]');
  const sidebar = qs('.filter-sidebar');

  if (toggle && sidebar) {
    const open = () => {
      if (sidebar.classList.contains('is-open')) return;
      sidebar.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      Overlay.show();
    };
    const close = () => {
      if (!sidebar.classList.contains('is-open')) return;
      sidebar.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      Overlay.hide();
    };
    toggle.addEventListener('click', () => {
      if (sidebar.classList.contains('is-open')) close(); else open();
    });
    qs('[data-filter-close]')?.addEventListener('click', close);
    document.addEventListener('overlay:clicked', close);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }

  /* Apply filters as soon as one changes (form has a noscript submit button) */
  on(document, 'change', '[data-filter-form] input, [data-filter-form] select', (e, input) => {
    const form = input.closest('form');
    if (form) (form.requestSubmit ? form.requestSubmit() : form.submit());
  });
}

/* ============================================================
   QuickAdd — product card quick-add handler
   Intercepts [data-quick-add-form] submit, POSTs to /cart/add.js,
   opens cart drawer on success, shows brief error state on failure.
   ============================================================ */

class QuickAdd {
  constructor() {
    this._bind();
  }

  _bind() {
    on(document, 'submit', '[data-quick-add-form]', (e, form) => {
      e.preventDefault();
      this._submit(form);
    });
  }

  async _submit(form) {
    const btn     = qs('[data-quick-add-btn]', form);
    const btnText = btn && qs('[data-btn-text]', btn);
    if (!btn || btn.disabled) return;

    const origText = btnText ? btnText.textContent : STRINGS.addToCart;
    btn.disabled = true;
    if (btnText) btnText.textContent = STRINGS.adding;

    try {
      const res = await fetch(`${ROUTES.cart_add_url}.js`, {
        method:  'POST',
        headers: { Accept: 'application/json' },
        body:    new FormData(form),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || STRINGS.cartError);
      }

      announce(STRINGS.cartUpdated);
      document.dispatchEvent(new CustomEvent('cart:updated'));
      window._cartDrawer?.open();

      if (btnText) btnText.textContent = STRINGS.added;
      setTimeout(() => {
        btn.disabled = false;
        if (btnText) btnText.textContent = origText;
      }, 1800);

    } catch (_err) {
      if (btnText) btnText.textContent = STRINGS.quickAddFailed;
      setTimeout(() => {
        btn.disabled = false;
        if (btnText) btnText.textContent = origText;
      }, 2200);
    }
  }
}

/* ============================================================
   VariantButtonState
   Keeps the .is-selected class on variant button labels in sync
   with the checked radio input, and updates the displayed
   selected-value text next to the option label.
   ============================================================ */

function initVariantButtonState() {
  on(document, 'change', '[data-variant-picker] .variant-btns input[type="radio"]', (e, radio) => {
    const group = radio.closest('.variant-option');
    if (!group) return;

    /* Toggle .is-selected on all sibling labels */
    qsa('.variant-btn', group).forEach(label => {
      const input = qs('input[type="radio"]', label);
      label.classList.toggle('is-selected', input?.checked === true);
    });

    /* Update the displayed selected value text */
    const valueDisplay = qs('[data-option-selected]', group);
    if (valueDisplay) valueDisplay.textContent = radio.value;
  });
}

/* ============================================================
   GalleryVariantSync
   Listens for the variant:changed CustomEvent (dispatched by
   VariantPicker with bubbles:true) at the document level and
   swaps the main product image to the variant's featured image.
   Document-level so it works across sibling sections.
   ============================================================ */

function initGalleryVariantSync() {
  document.addEventListener('variant:changed', (e) => {
    const fi = e.detail?.variant?.featured_image;
    if (!fi) return;

    const mainImg = qs('[data-gallery-main]');
    const thumbs  = qsa('[data-gallery-thumb]');
    if (!mainImg || !thumbs.length) return;

    const target = thumbs.find(t => t.dataset.mediaId === String(fi.id));
    if (!target) return;

    /* Swap active thumb */
    thumbs.forEach(t => t.classList.remove('is-active'));
    target.classList.add('is-active');

    /* Update main image with a brief fade */
    mainImg.style.opacity = '0';
    requestAnimationFrame(() => {
      if (target.dataset.src)    mainImg.src    = target.dataset.src;
      if (target.dataset.srcset) mainImg.srcset = target.dataset.srcset;
      if (target.dataset.alt)    mainImg.alt    = target.dataset.alt;
      mainImg.style.transition   = 'opacity 200ms ease';
      mainImg.style.opacity      = '1';
    });
  });
}

/* ============================================================
   CollectionSort
   Responds to the sort-by dropdown on collection pages.
   Updates the URL with ?sort_by= and reloads the page so
   Shopify's native sorting takes effect.
   ============================================================ */

function initCollectionSort() {
  on(document, 'change', '[data-collection-sort]', (e, select) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    /* Reset to page 1 when sort changes */
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  });
}

/* ============================================================
   Product media thumbnail gallery
   Delegated handler for [data-gallery-thumb] buttons inside
   [data-gallery] (sections/product-media.liquid).
   ============================================================ */

function initProductMediaThumbs() {
  on(document, 'click', '[data-gallery-thumb]', (e, thumb) => {
    const gallery = thumb.closest('[data-gallery]');
    if (!gallery) return;

    const mainImg = qs('[data-gallery-main]', gallery);
    const thumbs  = qsa('[data-gallery-thumb]', gallery);

    thumbs.forEach(t => t.classList.remove('is-active'));
    thumb.classList.add('is-active');

    if (mainImg) {
      mainImg.style.opacity  = '0';
      mainImg.style.transition = 'opacity 180ms ease';
      requestAnimationFrame(() => {
        if (thumb.dataset.src)    mainImg.src    = thumb.dataset.src;
        if (thumb.dataset.srcset) mainImg.srcset = thumb.dataset.srcset;
        if (thumb.dataset.alt)    mainImg.alt    = thumb.dataset.alt;
        mainImg.style.opacity = '1';
      });
    }
  });
}

/* ============================================================
   Hero background videos — lazy-loaded
   Sources ship with data-src only (preload="none"); the video is
   loaded and played when the hero nears the viewport, and paused
   again while off-screen. Skipped entirely under reduced motion
   (CSS also hides the element, leaving the poster/image).
   ============================================================ */

function initHeroVideos() {
  const videos = qsa('[data-hero-video]');
  if (!videos.length) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const load = (video) => {
    if (video.dataset.loaded) return;
    video.dataset.loaded = '1';
    qsa('source', video).forEach(source => {
      if (source.dataset.src) {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      }
    });
    video.load();
  };

  if (!('IntersectionObserver' in window)) {
    videos.forEach(v => { load(v); v.play().catch(() => {}); });
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const video = entry.target;
      if (entry.isIntersecting) {
        load(video);
        video.play().catch(() => {});
      } else if (video.dataset.loaded) {
        video.pause();
      }
    });
  }, { rootMargin: '200px' });

  videos.forEach(v => observer.observe(v));
}

/* ============================================================
   Bootstrap
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {
  Overlay.init();

  window._cartDrawer    = new CartDrawer();
  window._mobileMenu    = new MobileMenu();
  window._stickyHeader  = new StickyHeader();
  window._searchOverlay = new SearchOverlay();
  window._quickAdd      = new QuickAdd();

  /* Product page */
  qsa('[data-product-form]').forEach(form  => new ProductForm(form));
  qsa('[data-variant-picker]').forEach(el  => new VariantPicker(el));

  /* Utilities */
  initQuantityInputs();
  initCollapsibles();
  initProductTabs();
  initNavDropdowns();
  initScrollAnimations();
  initHeroVideos();
  initAnnouncementBar();
  initFilterToggle();
  initVariantButtonState();
  initGalleryVariantSync();
  initProductMediaThumbs();
  initCollectionSort();
});
